import type { I18n } from "@lingui/core";
import { msg } from "@lingui/core/macro";

import type { ApiErrorBody, ApiErrorCode } from "./apiErrorCodes";

type Params<C extends ApiErrorCode> = Extract<ApiErrorBody, { error: C }>["params"];

// Перевод кода ошибки: функция получает `params` своего кода и текущий i18n.
// Добавили код на бэкенде — `pnpm typecheck` упадёт, пока для него нет текста.
// Множественное число — ICU-строкой со значениями в дескрипторе: макрос `plural` внутри
// `msg({ id, message })` в Lingui 6 теряет значение (`{0}` без values — в тексте `NaN`).
type ApiErrorMessages = { [C in ApiErrorCode]: (params: Params<C>, i18n: I18n) => string };

const formatDateTime = (value: string, i18n: I18n): string =>
  new Intl.DateTimeFormat(i18n.locale, {
    day: "numeric",
    month: "long",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value));

export const apiErrorMessages: ApiErrorMessages = {
  not_authenticated: (_params, i18n) =>
    i18n._(
      msg({
        id: "errors.not_authenticated",
        message: "Your session has expired. Sign in with Steam again.",
      }),
    ),
  forbidden: (_params, i18n) =>
    i18n._(msg({ id: "errors.forbidden", message: "You are not allowed to do this." })),
  not_found: (_params, i18n) =>
    i18n._(
      msg({
        id: "errors.not_found",
        message: "Nothing here: it was deleted or belongs to another user.",
      }),
    ),
  validation_error: (_params, i18n) =>
    i18n._(
      msg({
        id: "errors.validation_error",
        message: "The server rejected the request. Reload the page and try again.",
      }),
    ),
  throttled: ({ wait }, i18n) => {
    if (wait === null) {
      return i18n._(
        msg({ id: "errors.throttled", message: "Too many requests. Try again a bit later." }),
      );
    }
    return i18n._({
      ...msg({
        id: "errors.throttled_wait",
        message:
          "{seconds, plural, one {Too many requests. Try again in # second.} other {Too many requests. Try again in # seconds.}}",
      }),
      values: { seconds: Math.ceil(wait) },
    });
  },
  match_not_found: ({ match_id: matchId }, i18n) =>
    i18n._(
      msg({
        id: "errors.match_not_found",
        message: `Match ${matchId} was not found in Deadlock data.`,
      }),
    ),
  analysis_limit_reached: ({ limit, resets_at }, i18n) => {
    const resetsAt = formatDateTime(resets_at, i18n);
    return i18n._(
      msg({
        id: "errors.analysis_limit_reached",
        message: `Free analyses for today are used up (limit: ${limit}). The next one is available on ${resetsAt}.`,
      }),
    );
  },
  match_not_ready: ({ retry_after_s }, i18n) => {
    return i18n._({
      ...msg({
        id: "errors.match_not_ready",
        message:
          "{minutes, plural, one {The match data is not published yet. Try again in # minute.} other {The match data is not published yet. Try again in # minutes.}}",
      }),
      values: { minutes: Math.max(1, Math.ceil(retry_after_s / 60)) },
    });
  },
  confirmation_required: (_params, i18n) =>
    i18n._(
      msg({
        id: "errors.confirmation_required",
        message: "This action needs confirmation. Reload the page and try again.",
      }),
    ),
  game_mode_not_supported: (_params, i18n) =>
    i18n._(
      msg({
        id: "errors.game_mode_not_supported",
        message: "Analysis is only available for regular 6v6 matches, not Street Brawl.",
      }),
    ),
  billing_disabled: (_params, i18n) =>
    i18n._(
      msg({
        id: "errors.billing_disabled",
        message: "Payments are not available yet. Try again later.",
      }),
    ),
  autopay_already_active: (_params, i18n) =>
    i18n._(
      msg({
        id: "errors.autopay_already_active",
        message: "Autopay is already on. You can turn it off on the Plans page.",
      }),
    ),
  autopay_not_found: (_params, i18n) =>
    i18n._(
      msg({
        id: "errors.autopay_not_found",
        message: "Autopay is already off.",
      }),
    ),
  clip_limit_reached: ({ limit, resets_at }, i18n) => {
    const resetsAt = formatDateTime(resets_at, i18n);
    return i18n._(
      msg({
        id: "errors.clip_limit_reached",
        message: `Clips for today are used up (limit: ${limit}). The next one is available on ${resetsAt}.`,
      }),
    );
  },
  clips_unavailable: (_params, i18n) =>
    i18n._(
      msg({
        id: "errors.clips_unavailable",
        message: "Clips are temporarily unavailable. Try again later.",
      }),
    ),
};
